"use client";

import { useState, useEffect, Fragment } from "react";
import { useTranslations } from "next-intl";
import { IntlLink } from "@/lib/utils/navigation";
import { cn } from "@/lib/utils/style";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "@/components/ui/breadcrumb";
import { ChevronRight, Home } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

export interface BreadcrumbItemData {
  label: string;
  href: string;
}

interface BreadcrumbUIProps {
  items: BreadcrumbItemData[];
  className?: string;
  isServerRendered?: boolean;
}

export default function BreadcrumbUI({
  items,
  className,
  isServerRendered = false,
}: BreadcrumbUIProps) {
  const t = useTranslations("breadcrumb");
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Reset expansion state when items change (route navigation)
  useEffect(() => {
    if (!isServerRendered) {
      setIsExpanded(false);
    }
  }, [items, isServerRendered]);

  const exceedsLimit = items.length > 3;
  const showEllipsis = exceedsLimit && !isExpanded && isMobile;

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList
        className={cn(
          "text-base text-macaron-app-charcoal-100 gap-3",
          !isExpanded ? "flex-nowrap" : "flex-wrap",
        )}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isFirst = index === 0;
          const isHome = item.href === "/";

          // 如果处于折叠状态，且不是第一个也不是最后一个元素，则隐藏
          if (showEllipsis && !isFirst && !isLast) {
            // 仅在第二个元素处渲染省略号
            if (index === 1) {
              return (
                <Fragment key="ellipsis">
                  <BreadcrumbItem className="text-base flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsExpanded(true)}
                      className="flex items-center justify-center p-2 -my-2 -mx-1 transition-colors hover:text-macaron-app-charcoal-800"
                      aria-label={t("expandPath")}
                    >
                      <BreadcrumbEllipsis className="h-6 w-6" />
                    </button>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="flex-shrink-0">
                    <ChevronRight className="!w-6 !h-6" />
                  </BreadcrumbSeparator>
                </Fragment>
              );
            }
            return null;
          }

          return (
            <Fragment key={`${item.href}-${index}`}>
              <BreadcrumbItem className="text-base min-w-0">
                {isLast ? (
                  <BreadcrumbPage className="text-macaron-app-charcoal-800 truncate max-w-[150px] sm:max-w-[300px] md:max-w-none block">
                    {isHome ? (
                      <>
                        <Home className="h-6 w-6 flex-shrink-0 inline-block mr-1 align-sub" aria-hidden="true" />
                        <span className="sr-only">{t("home")}</span>
                      </>
                    ) : (
                      item.label
                    )}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild className="hover:text-macaron-app-charcoal-800 truncate max-w-[120px] sm:max-w-[200px] md:max-w-none block">
                    <IntlLink href={item.href}>
                      {isHome ? (
                        <>
                          <Home className="h-6 w-6 flex-shrink-0 inline-block mr-1 align-sub" aria-hidden="true" />
                          <span className="sr-only">{t("home")}</span>
                        </>
                      ) : (
                        item.label
                      )}
                    </IntlLink>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator className="flex-shrink-0">
                  <ChevronRight className="!w-6 !h-6" />
                </BreadcrumbSeparator>
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
